import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { FilterMatchMode } from 'primereact/api';
import { translatedMessage } from '../../service/LanguageService';
import GeneralUtils from '../../utilities/GeneralUtils';
import DataTableUtils from '../../utilities/DataTableUtils';
import useAuth from "../../hooks/useAuth";
import DeleteModalComponent from '../../components/DeleteModalComponent';
import { Calendar } from 'primereact/calendar';
import { AdministrationService } from '../../service/AdministrationService';
import HolidayEditDialog from './HolidayEditDialog';
import { ProgressSpinner } from 'primereact/progressspinner';

const HolidayList = () => {

    const [holidays, setHolidays] = useState([]);
    const [holiday, setHoliday] = useState(null);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [isLoading, setIsLoading] = useState(true);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const toast = useRef(null);
    const dt = useRef(null);
    const { auth } = useAuth();

    const administrationService = useMemo(() => new AdministrationService(), []);

    const [filters] = useState({
        'name': { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const hasPermission = (permissionName) => {
        if (auth && auth.user && auth.user.permissions) {
            return auth.user.permissions.indexOf(permissionName) > -1;
        } else {
            return false;
        }
    };

    useEffect(() => {
        let _date = new Date()
        setSelectedDate(_date)
        getHolidays(_date)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [administrationService]);

    const getHolidays = async (_date) => {
        await administrationService.getHolidayList(_date ? _date.getFullYear() : GeneralUtils.getCurrentYear())
            .then((_holidays) => {
                setHolidays(_holidays);
                setSelectedDate(_date)
                setIsLoading(false);
            })
            .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
    }

    const updateList = async (savedData, isNew) => {
        let updatedData = DataTableUtils.updateTableList(holidays, savedData, isNew)

        setHolidays(updatedData);
    };

    const showNewHolidayDialog = () => {
        setHoliday(null);
        setEditDialogVisible(true);
    };

    const showEditHoliday = (_holiday) => {
        setHoliday(_holiday);
        setEditDialogVisible(true);
    };

    const showDeleteHolidayDialog = (_holiday) => {
        setHoliday(_holiday);
        setDeleteDialogVisible(true);
    };

    const hideDeleteHolidayDialog = () => {
        setDeleteDialogVisible(false);
    };

    const deleteHoliday = () => {
        administrationService.deleteHoliday(holiday.id)
            .then(() => {
                let _holidays = holidays.filter(item => item.id !== holiday.id)
                setHolidays(_holidays)
                toast?.current?.show({ severity: 'success', summary: translatedMessage('generic.delete.success') });
            })
            .catch((error) => {
                error && toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            })
            .finally(() => {
                setDeleteDialogVisible(false);
                setHoliday(null);
            });
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    {hasPermission('APP_ADMINISTRATION') &&
                        <Button label={translatedMessage('generic.add')}
                            icon="pi pi-plus"
                            className="p-button-primary mr-2"
                            onClick={showNewHolidayDialog}
                        />
                    }
                </div>
            </React.Fragment>
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                {hasPermission('APP_ADMINISTRATION') &&
                    <Button icon="pi pi-pencil" className="p-button-rounded p-button-info m-1" onClick={() => showEditHoliday(rowData)}
                        tooltip={translatedMessage("generic.edit")} tooltipOptions={{ showOnDisabled: true, position: 'top' }} />
                }
                {hasPermission('APP_ADMINISTRATION') &&
                    <Button icon="pi pi-trash" className="p-button-rounded p-button-danger m-1" onClick={() => showDeleteHolidayDialog(rowData)}
                        tooltip={translatedMessage("generic.delete")} tooltipOptions={{ showOnDisabled: true, position: 'top' }} />
                }
            </div>
        );
    };

    const calendarRowFilterTemplate = () => {
        return <Calendar value={selectedDate} view="year" dateFormat="yy"
            onChange={(e) => getHolidays(e.value)} />
    };

    if (isLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid h-full">
                    <div className="col-12">
                        <div className="card h-full">
                            <h3 className='mb-2'>{translatedMessage('holiday.list')}</h3>

                            <Toolbar left={leftToolbarTemplate} right={<></>} className="px-0 pt-0"></Toolbar>

                            <DataTable
                                ref={dt}
                                value={holidays}
                                dataKey="id"
                                paginator
                                rows={DataTableUtils.defalRowsPerPage()}
                                rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                                className="datatable-responsive pcn-datatable"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate={"{first} - {last} " + translatedMessage('generic.of') + " {totalRecords}"}
                                emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                responsiveLayout="stack"
                                filterDisplay="row"
                                filters={filters}
                                globalFilterFields={['name']}
                            >
                                <Column
                                    field="name"
                                    header={translatedMessage('generic.nameRo')}
                                    sortable
                                    headerStyle={{ width: '40%', minWidth: '10rem' }}
                                    filter
                                    showFilterMenu={false}
                                />
                                <Column
                                    field="date"
                                    header={translatedMessage('holiday.date')}
                                    sortable
                                    headerStyle={{ width: '20%', minWidth: '10rem' }}
                                    body={(e) => GeneralUtils.formatDate(e.date)}
                                    filter
                                    showFilterMenu={false}
                                    filterElement={calendarRowFilterTemplate}
                                    showClearButton={true}
                                />
                                <Column body={actionBodyTemplate}></Column>
                            </DataTable>

                            {editDialogVisible &&
                                <HolidayEditDialog
                                    value={holiday}
                                    visible={editDialogVisible}
                                    visibleSetter={setEditDialogVisible}
                                    afterSave={updateList}
                                />
                            }

                            {deleteDialogVisible &&
                                <DeleteModalComponent
                                    visible={deleteDialogVisible}
                                    item={holiday?.name}
                                    closeDialog={hideDeleteHolidayDialog}
                                    deleteRecord={() => deleteHoliday()}
                                />
                            }
                        </div>
                    </div>
                </div>
            </>
        );
    }
};

export default HolidayList;
